import { zodResolver } from "@hookform/resolvers/zod";
import AsyncSelect from "react-select/async";
import {
  Button,
  Checkbox,
  debounce,
  FormControlLabel,
  FormHelperText,
  Grid,
  TextField,
  Tooltip,
} from "@mui/material";
import { Box } from "@mui/material";
import {
  AsyncOption,
  IAsyncDropdownModel,
  IDropdownModel,
} from "common/models/IDropdownModel";
import { GetDepartmentValuesByInstitute } from "features/department/api/hooks";
import { GetProgramByInstituteDepartment } from "features/program/api/hooks";
import { useEffect, useRef, useState } from "react";
import { GetInstituteByLibrary } from "features/institute/api/hooks";
import { ProgramModelForDropdown } from "features/program/api/types";
import { SelectInstance } from "react-select";
import { SelectField } from "components/@extended/SelectField";
import { IMemberBarCodeSchema, MemberBarCodeSchema } from "../api/types";
import MainCard from "components/MainCard";
import AnimateButton from "components/@extended/AnimateButton";
import LoadingButton from "@mui/lab/LoadingButton";
import { CloudDownloadOutlined, InfoCircleFilled } from "@ant-design/icons";
import api from "utils/api";
import useAuth from "hooks/useAuth";
import { Controller, useForm } from "react-hook-form";
import RadioButtonGroup from "components/RadioButtonGroup";
import { GetMemberStatusValues } from "features/memberStatus/api/hooks";
import { GetMemberTypeValues } from "features/memberType/api/hooks";
import requests from "common/requests/asyncRequests";
import { GetAdmissionYearValues } from "features/admissionYear/api/hooks";

const MemberBarCodes = () => {
  const { modules } = useAuth();
  const materialRef = useRef<SelectInstance<IAsyncDropdownModel> | null>(null);
  const rackRef = useRef<SelectInstance<IAsyncDropdownModel> | null>(null);
  const supplierRef = useRef<SelectInstance<IDropdownModel> | null>(null);
  const [loading, setLoading] = useState(false);
  const [isReportsChecked, setIsReportsChecked] = useState(false);
  const [isBorderChecked, setIsBorderChecked] = useState(false);

  const handleIsReportsChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIsReportsChecked(event.target.checked);
  };
  const handleIsBorderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsBorderChecked(event.target.checked);
  };
  const apiURL = "api/Barcode/memberBarCodes";
  const fileName = "MemberBarcodes";
  const methods = useForm<IMemberBarCodeSchema>({
    mode: "all",
    resolver: zodResolver(MemberBarCodeSchema),
    defaultValues: {
      filterRadioBtn: 1,
      isReports: false,
      isBorder: false,
      memberCodeValueType: 1,
    },
  });

  const {
    register,
    reset,
    handleSubmit,
    control,
    watch,
    formState: { isSubmitting, errors },
  } = methods;

  const handleClearFilter = () => {
    reset();
    setIsReportsChecked(false);
    setIsBorderChecked(false);
    materialRef.current.clearValue();
    rackRef.current.clearValue();
    supplierRef.current.clearValue();
  };

  const onSubmit = handleSubmit(async (data) => {
    setLoading(true);
    if (isSubmitting) return;
    if (data.memberCodeValueType == 2) {
      data.memberCodeValues = null;
      data.memberCodeStringValue = data.memberCodeStringValue?.trim();
      if (data.memberCodeStringValue) {
        data.memberCodeStringValue = data.memberCodeStringValue
          .replace(/[\r\n\t\s]+/g, " ")
          .trim()
          ?.split(/[\r\n\t\s,]+/g) // split on newlines, spaces, or commas
          .filter((item) => item.trim() !== "")
          .join("\n");
      }
      data.memberCodeString = data.memberCodeStringValue
        ?.split(/[\r\n\t\s]/g)
        .filter((item) => item.trim() != "");
    } else if (data.memberCodeValueType == 1) {
      data.memberCodeString = null;
      data.memberCodeValues = data.memberCode?.map((c) => String(c.value));
    }
    api.downloadZip(apiURL, fileName, data, setLoading);
  });

  const { data: admissionYearQuery } = GetAdmissionYearValues();
  const [admissionYearOptions, setAdmissionYearOptions] = useState<
    IDropdownModel[]
  >([]);
  useEffect(() => {
    setAdmissionYearOptions(admissionYearQuery?.apiData || []);
  }, [admissionYearQuery]);

  const [programModel, setProgramModel] = useState<ProgramModelForDropdown>();
  const { data: instituteQuery } = GetInstituteByLibrary();
  const [instituteOptions, setInstituteOptions] = useState<IDropdownModel[]>(
    [],
  );
  useEffect(() => {
    setInstituteOptions(instituteQuery?.apiData || []);
  }, [instituteQuery]);
  const instituteID = watch("instituteID");
  const departmentID = watch("departmentID");

  const { data: departmentQuery } = GetDepartmentValuesByInstitute(instituteID);
  const [departmentOptions, setDepartmentOptions] = useState<IDropdownModel[]>(
    [],
  );
  useEffect(() => {
    setDepartmentOptions(departmentQuery?.apiData || []);
  }, [departmentQuery]);

  useEffect(() => {
    if (departmentID == null || departmentID == undefined) {
      setProgramOptions([]);
    }

    if (instituteID == null || instituteID == undefined) {
      setDepartmentOptions([]);
      setProgramOptions([]);
    }
  }, [instituteID, departmentID]);

  useEffect(() => {
    setProgramModel({
      instituteID: instituteID,
      departmentID: departmentID,
    });
  }, [instituteID, departmentID]);

  const { data: programQuery } = GetProgramByInstituteDepartment(programModel);
  const [programOptions, setProgramOptions] = useState<IDropdownModel[]>([]);

  useEffect(() => {
    if (programQuery?.status == 200) {
      setProgramOptions(programQuery?.apiData || []);
    }
  }, [programQuery]);

  const { data: memberTypeQuery } = GetMemberTypeValues();
  const [memberTypeOptions, setMemberTypeOptions] = useState<IDropdownModel[]>(
    [],
  );

  useEffect(() => {
    setMemberTypeOptions(memberTypeQuery?.apiData || []);
  }, [memberTypeQuery]);

  const { data: memberStatusQuery } = GetMemberStatusValues();
  const [memberStatusOptions, setMemberStatusOptions] = useState<
    IDropdownModel[]
  >([]);

  useEffect(() => {
    setMemberStatusOptions(memberStatusQuery?.apiData || []);
  }, [memberStatusQuery]);

  const barcodeFormatOptions = [
    { id: 1, name: "A4 Portrait - 10 Rows * 4 Columns" },
    { id: 2, name: "A4 Portrait - 12 Rows * 3 Columns" },
    { id: 3, name: "A4 Portrait - 14 Rows * 4 Columns" },
    { id: 4, name: "A4 Portrait - 5 Rows * 2 Columns" },
    { id: 5, name: "A4 Portrait - 13 Rows * 5 Columns" },
    { id: 6, name: "A4 Landscape - 8 Rows * 5 Columns" },
    { id: 7, name: "A4 Portrait - 12 Rows * 4 Columns" },
    { id: 8, name: "50 MM × 25 MM – Roll Label" },
  ];
  const memberCodeOptions: IAsyncDropdownModel[] = [];
  const handleMemberCodeSearch = debounce(async (query: string, callback) => {
    const options = await requests.getMemberCode(query, null);
    callback(options.apiData || []);
  }, 500);

  const memberCodeValueType = watch("memberCodeValueType");
  useEffect(() => {
    if (memberCodeValueType == 1) {
      methods.setValue("memberCode", null);
    }
    if (memberCodeValueType == 2) {
      methods.setValue("memberCodeString", null);
    }
  }, [memberCodeValueType]);
  return (
    <>
      <MainCard darkTitle title="Member Barcodes" darktitleVariant={"h4"}>
        {modules?.barCode?.permissions?.Add ? (
          <>
            <Box component="form" onSubmit={onSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={6} md={4} lg={4}>
                  <SelectField
                    options={admissionYearOptions}
                    control={control}
                    name="admissionYearID"
                    label="Student Admission Year"
                    placeholder="Select Student Admission Year"
                  />
                </Grid>
                <Grid item xs={6} md={4} lg={4}>
                  <SelectField
                    options={instituteOptions}
                    control={control}
                    name="instituteID"
                    label="Institute"
                    placeholder="Select Institute"
                  />
                </Grid>
                <Grid item xs={6} md={4} lg={4}>
                  <SelectField
                    options={departmentOptions}
                    control={control}
                    name="departmentID"
                    label="Department"
                    placeholder="Select Department"
                  />
                </Grid>
                <Grid item xs={6} md={4} lg={4}>
                  <SelectField
                    options={programOptions}
                    control={control}
                    name="programID"
                    label="Program"
                    placeholder="Select Program"
                  />
                </Grid>
                <Grid item xs={6} md={4} lg={4}>
                  <SelectField
                    control={control}
                    name="memberStatusID"
                    options={memberStatusOptions}
                    label="Member Status"
                    placeholder="Select Member Status"
                  />
                </Grid>
                <Grid item xs={6} md={4} lg={4}>
                  <SelectField
                    control={control}
                    name="memberTypeID"
                    options={memberTypeOptions}
                    label="Member Type"
                    placeholder="Select Member Type"
                  />
                </Grid>
                <Grid item xs={6} md={4} lg={4}>
                  <RadioButtonGroup
                    row
                    control={control}
                    name="filterRadioBtn"
                    options={[
                      {
                        id: "1",
                        label: "All",
                      },
                      {
                        id: "2",
                        label: "Activate",
                      },
                      {
                        id: "3",
                        label: "De-Activate",
                      },
                    ]}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={4} lg={4}>
                  <SelectField
                    options={barcodeFormatOptions}
                    control={control}
                    name="barCodeFormats"
                    label="Barcode Format*"
                    placeholder="Select Barcode Format"
                  />
                </Grid>
                {/* <Grid item xs={12} sm={12} md={4} lg={3}>
                  <SelectField
                    options={contentFormats}
                    control={control}
                    name="contentFormats"
                    label="Content Format*"
                    placeholder="Select Content Type"
                  />
                </Grid> */}
                <Grid item xs={6} sm={6} md={6} lg={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isReportsChecked}
                        onChange={handleIsReportsChange}
                      />
                    }
                    {...register("isReports")}
                    label="Do You Want a List for Generated Barcode ?"
                  />
                  <Tooltip
                    title="a saperate PDF will Generate with Generated Barcode (If Enabled)"
                    arrow
                  >
                    <InfoCircleFilled />
                  </Tooltip>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isBorderChecked}
                        onChange={handleIsBorderChange}
                      />
                    }
                    {...register("isBorder")}
                    label="Do You Want Border in Barcode ?"
                  />
                  <Tooltip
                    title="border will print in all side of barcode (If Enabled)"
                    arrow
                  >
                    <InfoCircleFilled />
                  </Tooltip>
                </Grid>
                <Grid item xs={12} md={3} lg={3} alignContent={"flex-end"}>
                  <RadioButtonGroup
                    row
                    control={control}
                    name="memberCodeValueType"
                    options={[
                      {
                        id: "1",
                        label: "Select Member Code",
                      },
                      {
                        id: "2",
                        label: "Enter Member Code",
                      },
                    ]}
                  />
                </Grid>
                <Grid item xs={12} md={12} lg={12}>
                  {memberCodeValueType == 2 && (
                    <>
                      <TextField
                        fullWidth
                        multiline
                        rows={6}
                        {...register("memberCodeStringValue")}
                        label="Member Code"
                        placeholder="Enter Member Code"
                        autoComplete="off"
                      />
                    </>
                  )}
                  {memberCodeValueType == 1 && (
                    <>
                      <Controller
                        name="memberCode"
                        control={control}
                        render={({ field: { onChange, value, ref } }) => (
                          <AsyncSelect
                            defaultValue={memberCodeOptions}
                            styles={{
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                              }),

                              control: (baseStyles) => ({
                                ...baseStyles,
                                borderColor: errors.memberCode
                                  ? "red"
                                  : baseStyles.borderColor,
                              }),
                            }}
                            isMulti
                            inputRef={ref}
                            getOptionLabel={(item) => item.label}
                            getOptionValue={(item) => item.value}
                            loadOptions={handleMemberCodeSearch}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            menuPlacement="bottom"
                            menuShouldScrollIntoView={false}
                            placeholder="Select Member Code"
                            value={value}
                            isClearable
                            closeMenuOnSelect={false}
                            onChange={(selected) => onChange(selected)}
                            noOptionsMessage={({ inputValue }) =>
                              !inputValue
                                ? "Type Something..."
                                : "No record found"
                            }
                          />
                        )}
                      />
                      <FormHelperText sx={{ color: "red" }}>
                        {errors.memberCode?.message}
                      </FormHelperText>
                    </>
                  )}
                </Grid>

                <Grid
                  container
                  item
                  direction={"row"}
                  xs={12}
                  md={6}
                  lg={6}
                  alignContent={"flex-end"}
                >
                  <AnimateButton>
                    <Tooltip
                      title="Downloading may take some time. Please do not refresh until the download is complete."
                      arrow
                    >
                      <LoadingButton
                        disableElevation
                        disabled={isSubmitting}
                        type="submit"
                        fullWidth
                        variant="contained"
                        loading={loading}
                        loadingPosition="start"
                        startIcon={<CloudDownloadOutlined />}
                      >
                        Download
                      </LoadingButton>
                    </Tooltip>
                  </AnimateButton>
                  <Button
                    title="Clear"
                    type="reset"
                    sx={{ mx: 1 }}
                    variant="contained"
                    color="secondary"
                    onClick={handleClearFilter}
                  >
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </>
        ) : (
          <></>
        )}
      </MainCard>
    </>
  );
};
export default MemberBarCodes;
